import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Column<T> {
  key: keyof T | string;
  header: string | React.ReactNode;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
  sortable?: boolean;
}

export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc' | null;
}

export interface RowSelection {
  selectedKeys: React.Key[];
  onChange: (keys: React.Key[]) => void;
}

export function getSelectAllState(
  pageKeys: React.Key[],
  selectedKeys: React.Key[],
): boolean | 'indeterminate' {
  if (pageKeys.length === 0) return false;
  const selected = new Set(selectedKeys);
  const selectedOnPage = pageKeys.filter((key) => selected.has(key));
  if (selectedOnPage.length === 0) return false;
  return selectedOnPage.length === pageKeys.length ? true : 'indeterminate';
}

export function toggleKey(selectedKeys: React.Key[], key: React.Key): React.Key[] {
  return selectedKeys.includes(key)
    ? selectedKeys.filter((k) => k !== key)
    : [...selectedKeys, key];
}

export function toggleAllKeys(
  pageKeys: React.Key[],
  selectedKeys: React.Key[],
): React.Key[] {
  if (pageKeys.length === 0) return selectedKeys;
  return getSelectAllState(pageKeys, selectedKeys) === true ? [] : [...pageKeys];
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  getRowKey?: (row: T, index: number) => React.Key;
  loading?: boolean;
  pagination?: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
  };
  sorting?: {
    field: string | null;
    direction: 'asc' | 'desc' | null;
    onSort: (field: string) => void;
  };
  className?: string;
  tableClassName?: string;
  iconClassName?: string;
  columnHeaderClassName?: string;
  emptyMessage?: string;
  onRowClick?: (row: T, index: number, event: React.MouseEvent<HTMLTableRowElement>) => void;
  onRowKeyActivate?: (row: T, index: number, event: React.KeyboardEvent<HTMLTableRowElement>) => void;
  withPagination?: boolean;
  withSorting?: boolean;
  selection?: RowSelection;
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  getRowKey,
  loading = false,
  pagination,
  sorting,
  className,
  tableClassName,
  iconClassName,
  columnHeaderClassName,
  emptyMessage = 'No data available',
  onRowClick,
  onRowKeyActivate,
  withPagination = true,
  withSorting = true,
  selection,
}: DataTableProps<T>) {
  const rowKeyOf = (row: T, index: number): React.Key =>
    getRowKey ? getRowKey(row, index) : index;
  const pageKeys = React.useMemo(
    () => data.map((row, index) => (getRowKey ? getRowKey(row, index) : index)),
    [data, getRowKey],
  );
  const selectAllState = selection
    ? getSelectAllState(pageKeys, selection.selectedKeys)
    : false;

  const renderHeaderCheckbox = (disabled: boolean) =>
    selection ? (
      <TableHead className="w-10">
        <Checkbox
          checked={selectAllState}
          disabled={disabled || pageKeys.length === 0}
          onCheckedChange={() =>
            selection.onChange(toggleAllKeys(pageKeys, selection.selectedKeys))
          }
          aria-label="Select all rows"
        />
      </TableHead>
    ) : null;

  const getCellValue = (row: T, key: keyof T | string): any => {
    if (typeof key === 'string' && key.includes('.')) {
      // Handle nested properties like 'user.name'
      return key.split('.').reduce((obj, prop) => obj?.[prop], row);
    }
    return row[key as keyof T];
  };

  const getSortDirectionForColumn = (columnKey: string) =>
    sorting?.field === columnKey ? sorting.direction : null;

  const getAriaSort = (
    columnKey: string,
  ): React.AriaAttributes['aria-sort'] => {
    const direction = getSortDirectionForColumn(columnKey);

    if (direction === 'asc') {
      return 'ascending';
    }
    if (direction === 'desc') {
      return 'descending';
    }
    return 'none';
  };

  const getSortIcon = (columnKey: string) => {
    const direction = getSortDirectionForColumn(columnKey);
    const iconClass = cn(
      "w-4 h-4",
      direction ? "text-[#826BFF]" : "text-[#737373]",
      iconClassName,
    );

    if (!direction) {
      return <ArrowUpDown className={iconClass} />;
    }
    if (direction === 'asc') {
      return <ArrowUp className={iconClass} />;
    }
    if (direction === 'desc') {
      return <ArrowDown className={iconClass} />;
    }
    return <ArrowUpDown className={iconClass} />;
  };

  const handleSort = (columnKey: string) => {
    if (sorting) {
      sorting.onSort(columnKey);
    }
  };

  const renderPagination = () => {
    if (!pagination) return null;

    const { currentPage, totalPages, onPageChange } = pagination;
    const pages = [];

    // Generate page numbers
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - 1 && i <= currentPage + 1)
      ) {
        pages.push(i);
      } else if (i === currentPage - 2 || i === currentPage + 2) {
        pages.push('...');
      }
    }

    return (
      <div className="flex items-center justify-center p-2">
        <div className="flex items-center gap-1">
          <Button
            variant="span"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1">
            {pages.map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="px-2 py-1 text-sm text-muted-foreground">...</span>
                ) : (
                  <Button
                    variant={currentPage === page ? 'outline' : 'ghost'}
                    size="sm"
                    onClick={() => onPageChange(page as number)}
                    className="h-9 w-9 p-0"
                  >
                    {page}
                  </Button>
                )}
              </React.Fragment>
            ))}
          </div>

          <Button
            variant="span"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={cn(className)}>
        <Table className={tableClassName}>
          <TableHeader>
            <TableRow className='h-10'>
              {renderHeaderCheckbox(true)}
              {columns.map((column, index) => {
                const columnKey = String(column.key);
                const sortDirection = getSortDirectionForColumn(columnKey);

                return (
                  <TableHead
                    key={index}
                    className={column.headerClassName ?? column.className}
                    aria-sort={
                      column.sortable && withSorting
                        ? getAriaSort(columnKey)
                        : undefined
                    }
                  >
                    {column.sortable && withSorting ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "h-auto p-0 hover:bg-transparent text-inherit text-xs font-medium",
                          sortDirection ? "text-[#171717]" : undefined,
                          columnHeaderClassName,
                        )}
                        onClick={() => handleSort(columnKey)}
                      >
                        {column.header}
                        <span className="ml-2">
                          {getSortIcon(columnKey)}
                        </span>
                      </Button>
                    ) : (
                      <span className={cn(columnHeaderClassName)}>
                        {column.header}
                      </span>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={index}>
                {selection ? (
                  <TableCell className="w-10">
                    <div className="h-4 w-4 bg-muted animate-pulse rounded" />
                  </TableCell>
                ) : null}
                {columns.map((_, colIndex) => (
                  <TableCell key={colIndex}>
                    <div className="h-4 bg-muted animate-pulse rounded" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <div>
        <Table className={tableClassName}>
          <TableHeader>
            <TableRow className='h-10'>
              {renderHeaderCheckbox(false)}
              {columns.map((column, index) => {
                const columnKey = String(column.key);
                const sortDirection = getSortDirectionForColumn(columnKey);

                return (
                  <TableHead
                    key={index}
                    className={column.headerClassName ?? column.className}
                    aria-sort={
                      column.sortable && withSorting
                        ? getAriaSort(columnKey)
                        : undefined
                    }
                  >
                    {column.sortable && withSorting ? (
                      <Button
                        variant="span"
                        size="sm"
                        className={cn(
                          "h-auto p-0 hover:bg-transparent text-inherit text-xs font-medium",
                          sortDirection ? "text-[#171717]" : undefined,
                          columnHeaderClassName,
                        )}
                        onClick={() => handleSort(columnKey)}
                      >
                        {column.header}
                        <span className="ml-2">
                          {getSortIcon(columnKey)}
                        </span>
                      </Button>
                    ) : (
                      <span className={cn(columnHeaderClassName)}>
                        {column.header}
                      </span>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selection ? 1 : 0)}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, rowIndex) => {
                const rowKey = rowKeyOf(row, rowIndex);
                const isSelected = selection
                  ? selection.selectedKeys.includes(rowKey)
                  : false;
                return (
                <TableRow
                  key={rowKey}
                  data-state={isSelected ? 'selected' : undefined}
                  onClick={onRowClick ? (event) => onRowClick(row, rowIndex, event) : undefined}
                  className={onRowClick ? "cursor-pointer hover:bg-muted/50" : undefined}
                  tabIndex={onRowKeyActivate ? 0 : undefined}
                  onKeyDown={
                    onRowKeyActivate
                      ? (event) => {
                          if (event.key === "Enter" && event.target === event.currentTarget) {
                            onRowKeyActivate(row, rowIndex, event);
                          }
                        }
                      : undefined
                  }
                >
                  {selection ? (
                    <TableCell className="w-10" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() =>
                          selection.onChange(toggleKey(selection.selectedKeys, rowKey))
                        }
                        aria-label="Select row"
                      />
                    </TableCell>
                  ) : null}
                  {columns.map((column, colIndex) => {
                    const value = getCellValue(row, column.key);
                    return (
                      <TableCell key={colIndex} className={column.className}>
                        {column.render ? column.render(value, row, rowIndex) : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {withPagination ? renderPagination() : null}
    </div>
  );
}
